import {
  createContext,
  useContext,
  useEffect,
} from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase.ts';
import type { MenuSnapshot } from '../lib/types.ts';

interface TenantContextValue {
  snapshot: MenuSnapshot;
  slug: string;
}

const TenantContext = createContext<TenantContextValue | null>(null);

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used inside TenantProvider');
  return ctx;
}

async function fetchMenuSnapshot(slug: string): Promise<MenuSnapshot> {
  const { data, error } = await supabase.rpc('get_menu_snapshot', { p_slug: slug });
  if (error) throw error;
  return data as MenuSnapshot;
}

export function TenantProvider() {
  const { slug } = useParams<{ slug: string }>();


  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['menu', slug],
    queryFn: () => fetchMenuSnapshot(slug!),
    enabled: !!slug,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });

  useEffect(() => {
    if (!data) return;
    const theme = data.tenant.theme;
    const root = document.documentElement;
    if (theme.mode === 'dark') {
      root.setAttribute('data-theme', 'dark');
    } else {
      root.removeAttribute('data-theme');
    }
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme.theme_color ?? theme.accent);
  }, [data]);

  if (isLoading) return <AppShellSkeleton />;

  if (isError || !data) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-lg font-semibold" style={{ color: 'var(--text)' }}>
          Не удалось загрузить меню
        </p>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          {(error as Error)?.message ?? 'Проверьте интернет и попробуйте снова'}
        </p>
        <button
          className="rounded-lg px-5 py-2.5 text-sm font-medium"
          style={{ background: 'var(--accent)', color: 'var(--accent-fg)' }}
          onClick={() => window.location.reload()}
        >
          Обновить
        </button>
      </div>
    );
  }

  return (
    <TenantContext.Provider value={{ snapshot: data, slug: slug! }}>
      <Outlet />
    </TenantContext.Provider>
  );
}

function AppShellSkeleton() {
  return (
    <div
      className="flex min-h-dvh flex-col"
      style={{ background: 'var(--bg)' }}
      aria-busy="true"
      aria-label="Загрузка меню"
    >
      {/* Header skeleton */}
      <div
        className="h-14 w-full animate-pulse"
        style={{ background: 'var(--surface)' }}
      />
      {/* Category nav skeleton */}
      <div
        className="flex gap-2 overflow-hidden px-4 py-3"
        style={{ background: 'var(--surface)' }}
      >
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="h-8 w-20 flex-shrink-0 animate-pulse rounded-full"
            style={{ background: 'var(--border)' }}
          />
        ))}
      </div>
      {/* Cards skeleton */}
      <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="animate-pulse rounded-xl"
            style={{ background: 'var(--surface)', height: 220 }}
          />
        ))}
      </div>
    </div>
  );
}
